import React, { forwardRef, type ReactNode } from 'react';

import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { PopupTrigger } from '@atlaskit/popup/experimental';
import { Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { MenuItemBase } from '../menu-item';
import type { MenuItemCommonProps, MenuItemOnClick } from '../types';

export type FlyoutMenuItemTriggerProps = MenuItemCommonProps & {
	// TODO(BLU-3861): rename this to `elemBefore` to align with the other menu items.
	iconBefore?: ReactNode;

	/**
	 * Called when the user has clicked on the trigger.
	 */
	onClick?: MenuItemOnClick<HTMLButtonElement>;

	/**
	 * Indicates that the menu item is selected.
	 */
	isSelected?: boolean;
};

/**
 * __FlyoutMenuItemTrigger__
 *
 * The button that toggles the flyout menu.
 */
export const FlyoutMenuItemTrigger = forwardRef<HTMLButtonElement, FlyoutMenuItemTriggerProps>(
	(
		{
			children,
			iconBefore,
			onClick,
			isSelected,
			interactionName,
			testId,
			isContentTooltipDisabled,
			visualContentRef,
		},
		forwardedRef,
	) => (
		<PopupTrigger>
			{({
				ref,
				'aria-controls': ariaControls,
				'aria-expanded': ariaExpanded,
				'aria-haspopup': ariaHasPopup,
			}) => (
				<MenuItemBase
					testId={testId}
					ref={mergeRefs([ref, forwardedRef])}
					visualContentRef={visualContentRef}
					elemBefore={iconBefore}
					elemAfter={
						<Box paddingInline="space.075">
							<ChevronRightIcon label="" color={token('color.icon', '#44546F')} />
						</Box>
					}
					onClick={onClick}
					ariaControls={ariaControls}
					ariaExpanded={ariaExpanded}
					ariaHasPopup={ariaHasPopup}
					interactionName={interactionName}
					isContentTooltipDisabled={isContentTooltipDisabled}
					isSelected={isSelected}
				>
					{children}
				</MenuItemBase>
			)}
		</PopupTrigger>
	),
);
